import type { ReactNode } from 'react';

import { Button, type ButtonProps } from './Button';
import { Tooltip } from './Tooltip';

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'size'> {
  /** Required. An icon with no accessible name is invisible to a screen reader. */
  label: string;
  icon: ReactNode;
  size?: 'icon' | 'icon-sm';
  /** Set false only when the surrounding context already names the control. */
  showTooltip?: boolean;
}

/**
 * An icon-only control that cannot be built without an accessible name -- `label` is a
 * required prop precisely so the accessible version is the only version.
 */
export function IconButton({
  label,
  icon,
  size = 'icon',
  showTooltip = true,
  ...props
}: IconButtonProps) {
  const button = (
    <Button aria-label={label} size={size} {...props}>
      {icon}
    </Button>
  );

  if (!showTooltip) return button;
  return <Tooltip content={label}>{button}</Tooltip>;
}
